import { useSyncExternalStore } from 'react';
import { api, ApiError, errorText } from '../../api';
import { t, tx } from '../../i18n/t';
import type { UploadResponse } from './types';

// Auswahl vor dem Hochladen und laufende Uploads. Liegt außerhalb der Seiten, damit beides beim
// Wechsel zwischen Ordnern erhalten bleibt. Jede Datei geht einzeln hoch (höchstens drei gleichzeitig),
// so hat jede ihren eigenen Stand und kann einzeln wiederholt werden.

export interface UploadItem {
  id: string;
  key: string; // Ordner: <Firma>|<Jahr>|<Monat>|<Ordner>
  url: string;
  file: File;
  status: 'wait' | 'uploading' | 'error' | 'done';
  error?: string;
  retry?: boolean; // Netzwerk- oder Serverfehler: noch einmal versuchen lohnt sich
  belegId?: string;
  batch: string;
}

export interface UploadState {
  pending: { key: string; files: File[] } | null;
  items: UploadItem[];
}

let state: UploadState = { pending: null, items: [] };
const subs = new Set<() => void>();
// Ergebnis je Auftrag („Hochladen“, „Fertig“ bei Sammelfotos, „Erneut versuchen“), unabhängig von state.items:
// Fertige Uploads verschwinden dort schon, sobald der Beleg vom Server in der Liste steht (prune).
interface Batch {
  notify: (msg: string) => void;
  open: number; // noch nicht fertig
  done: UploadItem[];
  failed: UploadItem[];
}
const batches = new Map<string, Batch>();
let seq = 0;
const MAX_PARALLEL = 3;

function set(next: Partial<UploadState>) {
  state = { ...state, ...next };
  for (const s of subs) s();
}

export function useUploads(): UploadState {
  return useSyncExternalStore(
    (cb) => {
      subs.add(cb);
      return () => subs.delete(cb);
    },
    () => state,
  );
}

const fileKey = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;

// Dateien zur Bestätigung vormerken. Schon gewählte Dateien (gleicher Name, Größe, Datum) kommen nicht doppelt.
export function stage(key: string, files: File[], notify: (msg: string) => void) {
  if (!files.length) return;
  const prev = state.pending?.key === key ? state.pending.files : [];
  const known = new Set(prev.map(fileKey));
  const fresh: File[] = [];
  let dup = 0;
  for (const f of files) {
    const k = fileKey(f);
    if (known.has(k)) dup++;
    else {
      known.add(k);
      fresh.push(f);
    }
  }
  if (dup) notify(dup === 1 ? t('Diese Datei ist schon ausgewählt') : t('{n} Dateien sind schon ausgewählt', { n: dup }));
  if (!fresh.length && prev.length) return;
  set({ pending: { key, files: [...prev, ...fresh] } });
}

export function clearPending() {
  set({ pending: null });
}

export function removePending(index: number) {
  const p = state.pending;
  if (!p) return;
  const files = p.files.filter((_, i) => i !== index);
  set({ pending: files.length ? { ...p, files } : null });
}

// Hochladen starten; notify meldet am Ende das Ergebnis (Meldung unten am Bildschirm)
export function upload(key: string, url: string, files: File[], notify: (msg: string) => void) {
  if (!files.length) return;
  const batch = `b${++seq}`;
  batches.set(batch, { notify, open: files.length, done: [], failed: [] });
  const items: UploadItem[] = files.map((file) => ({ id: `u${++seq}`, key, url, file, status: 'wait', batch }));
  // neueste oben
  set({ items: [...items.reverse(), ...state.items] });
  pump();
}

export function retry(id: string, notify: (msg: string) => void) {
  // Läuft der ursprüngliche Auftrag noch, meldet er diese Datei nicht mehr als Fehler; das macht jetzt der neue.
  const before = state.items.find((i) => i.id === id);
  const old = before && batches.get(before.batch);
  if (old) old.failed = old.failed.filter((i) => i.id !== id);
  const batch = `b${++seq}`;
  batches.set(batch, { notify, open: 1, done: [], failed: [] });
  update(id, { status: 'wait', error: undefined, retry: undefined, batch });
  pump();
}

export function dismiss(id: string) {
  set({ items: state.items.filter((i) => i.id !== id) });
}

// Fertige Uploads entfernen, sobald der Beleg in der Liste vom Server steht
export function prune(key: string, serverIds: Set<string>) {
  const keep = state.items.filter((i) => !(i.key === key && i.status === 'done' && i.belegId && serverIds.has(i.belegId)));
  if (keep.length !== state.items.length) set({ items: keep });
}

function update(id: string, patch: Partial<UploadItem>) {
  set({ items: state.items.map((i) => (i.id === id ? { ...i, ...patch } : i)) });
}

function pump() {
  let running = state.items.filter((i) => i.status === 'uploading').length;
  // älteste zuerst
  for (const item of [...state.items].reverse()) {
    if (running >= MAX_PARALLEL) break;
    if (item.status !== 'wait') continue;
    running++;
    update(item.id, { status: 'uploading' });
    void send(item);
  }
}

async function send(item: UploadItem) {
  const fd = new FormData();
  fd.append('file', item.file, item.file.name);
  let ok = false;
  try {
    const res = await api.post<UploadResponse>(item.url, fd);
    const b = res.belege[0];
    ok = !!b;
    if (b) update(item.id, { status: 'done', belegId: b.id });
    else {
      const error = res.errors[0] ? tx(res.errors[0].error) : t('Nicht gespeichert.');
      update(item.id, { status: 'error', error, retry: false });
    }
  } catch (err) {
    const status = err instanceof ApiError ? err.status : 0;
    update(item.id, { status: 'error', error: errorText(err), retry: status === 0 || status >= 500 || status === 429 });
  }
  finish(item, ok);
  pump();
}

function finish(item: UploadItem, ok: boolean) {
  const b = batches.get(item.batch);
  if (!b) return;
  (ok ? b.done : b.failed).push(item);
  b.open -= 1;
  if (b.open > 0) return;
  batches.delete(item.batch);
  const { notify, done, failed } = b;
  if (failed.length) {
    notify(
      failed.length === 1
        ? t('Nicht gespeichert: {name}', { name: failed[0]!.file.name })
        : t('{n} Dateien nicht gespeichert', { n: failed.length }),
    );
  } else if (done.length === 1) {
    notify(t('Gespeichert: {name}', { name: done[0]!.file.name }));
  } else if (done.length) {
    notify(t('{n} Belege gespeichert', { n: done.length }));
  }
}
